import { useState, useMemo, useEffect } from 'react';
import { useSeoMeta } from '@unhead/react';
import { TombstoneLayout } from '@/components/TombstoneLayout';
import { EventCard } from '@/components/EventCard';
import { LoginArea } from '@/components/auth/LoginArea';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useUserEvents } from '@/hooks/useTombstone';
import { useAppContext } from '@/hooks/useAppContext';
import { useToast } from '@/hooks/useToast';
import { nip19 } from 'nostr-tools';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Search,
  RefreshCw,
  SlidersHorizontal,
  Trash2,
  FileText,
  Hash,
  List,
  ChevronRight,
  ChevronDown,
  Server,
  Users,
  VolumeX,
  Pin,
  Bookmark,
  FolderOpen,
  Library,
  Globe,
  X,
  MessageCircle,
  ThumbsUp,
  Repeat,
  Image,
  Film,
  Tv,
  PenTool,
  BookOpen,
  Code,
  BarChart3,
  ShoppingBag,
  Calendar as CalendarIcon,
  Radio,
  GitBranch,
  Layers,
  Megaphone,
  Tag,
  Award,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';

const kindLabels: Record<number, string> = {
  // Social
  1: 'Note',
  6: 'Repost',
  7: 'Reaction',
  16: 'Generic Repost',
  1111: 'Comment',
  // Media
  20: 'Picture',
  21: 'Video',
  22: 'Short Video',
  34235: 'Addressable Video',
  34236: 'Addressable Short Video',
  // Content
  24: 'Public Message',
  9802: 'Highlight',
  30818: 'Wiki Article',
  1337: 'Code Snippet',
  1068: 'Poll',
  1984: 'Report',
  1985: 'Label',
  10154: 'Podcast',
  // Long-form
  30023: 'Article',
  30024: 'Article Draft',
  31234: 'Draft Event',
  // Commerce
  30402: 'Classified Listing',
  30403: 'Draft Listing',
  // Live
  30311: 'Live Event',
  30312: 'Interactive Room',
  30313: 'Conference Event',
  // Calendar
  31922: 'Calendar Date Event',
  31923: 'Calendar Time Event',
  31924: 'Calendar',
  31925: 'Calendar RSVP',
  // Git
  30617: 'Git Repository',
  30618: 'Git Repository State',
  // Badges
  30009: 'Badge Definition',
  // Replaceable lists
  3: 'Follow List',
  10000: 'Mute List',
  10001: 'Pinned Notes',
  10002: 'Relay List',
  10003: 'Bookmarks',
  10004: 'Communities List',
  10005: 'Public Chats List',
  10006: 'Blocked Relays List',
  10007: 'Search Relays List',
  10009: 'User Groups',
  10015: 'Interests List',
  10020: 'Media Follows',
  10030: 'User Emoji List',
  10050: 'DM Relay List',
  10054: 'Favorite Podcasts',
  10063: 'User Server List',
  10064: 'Authored Podcasts',
  30315: 'User Status',
  // Addressable sets
  30000: 'Follow Set',
  30002: 'Relay Set',
  30003: 'Bookmark Set',
  30004: 'Curation Set',
  30005: 'Video Set',
  30006: 'Picture Set',
  30007: 'Kind Mute Set',
  30008: 'Badge Set',
  30015: 'Interest Set',
  30030: 'Emoji Set',
  30267: 'App Curation Set',
  39089: 'Starter Pack',
  39092: 'Media Starter Pack',
};

// Kind groupings for the tree view and stats
const SOCIAL_KINDS = [6, 7, 16, 1111];
const MEDIA_KINDS = [20, 21, 22, 34235, 34236];
const CONTENT_KINDS = [24, 9802, 30818, 1337, 1068, 1984, 1985, 10154];
const LONG_FORM_KINDS = [30023, 30024, 31234];
const COMMERCE_KINDS = [30402, 30403];
const LIVE_KINDS = [30311, 30312, 30313];
const CALENDAR_KINDS = [31922, 31923, 31924, 31925];
const GIT_KINDS = [30617, 30618];
const BADGE_KINDS = [30009];
const LIST_KINDS = [3, 10000, 10001, 10002, 10003, 10004, 10005, 10006, 10007, 10009, 10015, 10020, 10030, 10050, 10054, 10063, 10064, 30315];
const SET_KINDS = [30000, 30002, 30003, 30004, 30005, 30006, 30007, 30008, 30015, 30030, 30267, 39089, 39092];

// Public relays used for wider discovery scanning (does not affect user's NIP-65 list).
// Expanded for better coverage of where events may have propagated.
const DISCOVERY_RELAYS = [
  'wss://relay.primal.net',
  'wss://relay.damus.io',
  'wss://nos.lol',
  'wss://relay.nostr.band',
  'wss://nostr.wine',
  'wss://relay.ditto.pub',
  'wss://purple.pages.dev',
  'wss://relay.snort.social',
  'wss://eden.nostr.land',
  'wss://relay.current.fyi',
  'wss://nostr.rocks',
  'wss://relay.nostr.bg',
  'wss://nostr.mom',
  'wss://nostr.oxtr.dev',
  'wss://relay.bitcoiner.social',
  'wss://relay.nostr.wf',
  'wss://nostr-pub.wellorder.net',
  'wss://nostr.orangepill.dev',
] as const;

export default function Index() {
  useSeoMeta({
    title: 'Tombstone — Nostr Event Manager',
    description: 'Manage, delete, and resolve lingering Nostr events across relays.',
  });

  const { user } = useCurrentUser();
  const { config } = useAppContext();

  const [scanWider, setScanWider] = useState(false);
  const { data, isLoading, refetch, isFetching } = useUserEvents(user?.pubkey, scanWider);
  const { toast } = useToast();

  const [search, setSearch] = useState('');
  const [selectedKinds, setSelectedKinds] = useState<Set<number>>(new Set());
  const [openSections, setOpenSections] = useState<Set<string>>(new Set(['deleted']));
  const [accumulatedEvents, setAccumulatedEvents] = useState<any[]>([]);
  const [customDiscoveryRelays, setCustomDiscoveryRelays] = useState<string[]>([]);
  const [customInput, setCustomInput] = useState('');
  const [discoveryOpen, setDiscoveryOpen] = useState(false);

  // Clear accumulated events when scan mode changes so we don't mix normal + wide results
  useEffect(() => {
    setAccumulatedEvents([]);
  }, [scanWider]);

  const relayCount = config.relayMetadata.relays.length;
  const writeRelayCount = config.relayMetadata.relays.filter(r => r.write).length;

  // Accumulate events across refreshes so the count only grows.
  // Group by logical identity (addrCoord for addressable events, or event id) to avoid
  // showing both original and nullified versions of the same item.
  useEffect(() => {
    if (data?.events && data.events.length > 0) {
      setAccumulatedEvents((prev) => {
        const seen = new Set(prev.map(e => e.addrCoord || e.event.id));
        const newEvents = data.events.filter(e => {
          const key = e.addrCoord || e.event.id;
          return !seen.has(key);
        });
        return [...prev, ...newEvents];
      });
    }
  }, [data]);

  const allKinds = useMemo(() => {
    const events = accumulatedEvents.length > 0 ? accumulatedEvents : (data?.events || []);
    const kinds = new Set(events.map((e) => e.event.kind));
    return Array.from(kinds).sort((a, b) => a - b);
  }, [accumulatedEvents, data]);

  const allEvents = useMemo(() => {
    const sourceEvents = accumulatedEvents.length > 0 ? accumulatedEvents : (data?.events || []);
    let events = [...sourceEvents];

    // Selected kinds filter
    if (selectedKinds.size > 0) {
      events = events.filter((e) => selectedKinds.has(e.event.kind));
    }

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      events = events.filter((e) => {
        const title = e.event.tags.find(([name]) => name === 'title')?.[1]?.toLowerCase() ?? '';
        const content = e.event.content.toLowerCase();
        const id = e.event.id.toLowerCase();
        return title.includes(q) || content.includes(q) || id.includes(q);
      });
    }

    return events;
  }, [accumulatedEvents, data, selectedKinds, search]);

  const stats = useMemo(() => {
    const sourceEvents = accumulatedEvents.length > 0 ? accumulatedEvents : (data?.events || []);
    if (sourceEvents.length === 0) return null;

    const total = sourceEvents.length;
    const deleted = sourceEvents.filter((e) => e.isDeleted).length;
    const active = total - deleted;

    // Content categories
    const notes = sourceEvents.filter((e) => e.event.kind === 1).length;
    const articles = sourceEvents.filter((e) => LONG_FORM_KINDS.includes(e.event.kind)).length;
    const social = sourceEvents.filter((e) => SOCIAL_KINDS.includes(e.event.kind)).length;
    const media = sourceEvents.filter((e) => MEDIA_KINDS.includes(e.event.kind)).length;
    const content = sourceEvents.filter((e) => CONTENT_KINDS.includes(e.event.kind)).length;
    const lists = sourceEvents.filter((e) => LIST_KINDS.includes(e.event.kind)).length;
    const sets = sourceEvents.filter((e) => SET_KINDS.includes(e.event.kind)).length;
    const commerce = sourceEvents.filter((e) => COMMERCE_KINDS.includes(e.event.kind)).length;
    const calendar = sourceEvents.filter((e) => CALENDAR_KINDS.includes(e.event.kind)).length;
    const live = sourceEvents.filter((e) => LIVE_KINDS.includes(e.event.kind)).length;
    const git = sourceEvents.filter((e) => GIT_KINDS.includes(e.event.kind)).length;

    return { total, deleted, active, notes, articles, social, media, content, lists, sets, commerce, calendar, live, git };
  }, [accumulatedEvents, data]);

  const filterTabs: { key: FilterKind; label: string; count: number | undefined }[] = [
    { key: 'all', label: 'All', count: stats?.total },
    { key: 'notes', label: 'Notes', count: stats?.notes },
    { key: 'articles', label: 'Articles', count: stats?.articles },
    { key: 'social', label: 'Social', count: stats?.social },
    { key: 'media', label: 'Media', count: stats?.media },
    { key: 'content', label: 'Content', count: stats?.content },
    { key: 'lists', label: 'Lists', count: stats?.lists },
    { key: 'sets', label: 'Sets', count: stats?.sets },
    { key: 'commerce', label: 'Commerce', count: stats?.commerce },
    { key: 'calendar', label: 'Calendar', count: stats?.calendar },
    { key: 'live', label: 'Live', count: stats?.live },
    { key: 'git', label: 'Git', count: stats?.git },
    { key: 'deleted', label: 'Deleted', count: stats?.deleted },
  ];

  const toggleKind = (kind: number) => {
    setSelectedKinds((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  };

  const addCustomDiscoveryRelay = () => {
    const trimmed = customInput.trim();
    if (!trimmed || customDiscoveryRelays.includes(trimmed)) return;

    if (!trimmed.startsWith('wss://') && !trimmed.startsWith('ws://')) {
      toast({
        title: 'Invalid relay',
        description: 'Must start with wss:// or ws://',
        variant: 'destructive',
      });
      return;
    }

    setCustomDiscoveryRelays([...customDiscoveryRelays, trimmed]);
    setCustomInput('');
  };

  const removeCustomDiscoveryRelay = (url: string) => {
    setCustomDiscoveryRelays(customDiscoveryRelays.filter(r => r !== url));
  };

  return (
<TombstoneLayout>
  <div className="max-w-4xl mx-auto px-6 py-10">
    {/* Warning Banner */}
    <div className="flex items-center gap-3 p-4 mb-8 border border-accent/50 rounded-lg bg-transparent shadow-lg shadow-accent/20 backdrop-blur-sm">
      <img src="/tombstone_skull.png" alt="⚠️" className="w-10 h-10 flex-shrink-0" />
      <div>
        <h3 className="font-semibold text-accent text-lg leading-tight uppercase tracking-wider">WARNING!!!</h3>
        <p className="text-accent/90 text-sm leading-relaxed">
          Tombstone is destructive and experimental. You may permanently lose or destroy Nostr content. 
          Use with extreme caution. Always verify events before action.
        </p>
      </div>
    </div>
    {!user ? (
      <LandingHero />
    ) : (

          <div className="space-y-8 animate-fade-up">
            {/* Page header with relay info */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <h1 className="font-display text-2xl font-semibold text-foreground tracking-tight">
                  Event Manager
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  View, delete, and manage your Nostr events across relays.
                </p>
              </div>
              
              {/* Relay quantity indicator */}
              <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-card border border-border">
                <div className="flex items-center gap-2">
                  <Server className="w-4 h-4 text-[hsl(var(--accent))]" />
                  <span className="text-sm font-medium text-foreground">
                    {relayCount} {relayCount === 1 ? 'Relay' : 'Relays'}
                  </span>
                </div>
                <div className="w-px h-4 bg-border" />
                <span className="text-xs text-muted-foreground">
                  {writeRelayCount} write-enabled
                </span>
              </div>
            </div>

            {/* Public + Custom Relays Panel (collapsible) */}
            {user && (
              <Collapsible open={discoveryOpen} onOpenChange={setDiscoveryOpen}>
                <CollapsibleTrigger className="flex w-full items-center gap-2 py-3 px-4 hover:bg-secondary/50 rounded-xl group border border-border mb-3">
                  <ChevronRight className={cn("w-4 h-4 transition-transform", discoveryOpen ? "rotate-90" : "")} />
                  <Globe className="w-4 h-4 text-accent" />
                  <span className="font-medium">Custom Discovery Relays</span>
                  <span className="ml-auto text-xs bg-secondary px-3 py-1 rounded font-mono text-muted-foreground">
                    {DISCOVERY_RELAYS.length + customDiscoveryRelays.length}
                  </span>
                </CollapsibleTrigger>

                <CollapsibleContent className="mb-6">
                  <div className="border border-border rounded-xl bg-card p-5">
                    <div className="flex gap-2 mb-4">
                      <Input
                        value={customInput}
                        onChange={(e) => setCustomInput(e.target.value)}
                        placeholder="wss://your-custom-relay.com"
                        className="font-mono text-sm h-9 bg-secondary/50"
                        onKeyDown={(e) => e.key === 'Enter' && addCustomDiscoveryRelay()}
                      />
                      <Button onClick={addCustomDiscoveryRelay} size="sm" className="h-9 px-6">Add</Button>
                    </div>

                    <div className="space-y-2 max-h-80 overflow-auto pr-2">
                      {[...DISCOVERY_RELAYS, ...customDiscoveryRelays].map((relay) => {
                        const isCustom = customDiscoveryRelays.includes(relay);
                        return (
                          <div key={relay} className="flex items-center gap-3 px-4 py-2.5 bg-secondary/60 rounded-lg border border-border/50 group">
                            <div className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0 animate-pulse" />
                            <code className="font-mono text-xs flex-1 text-muted-foreground truncate">{relay}</code>
                            {isCustom && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeCustomDiscoveryRelay(relay)}
                                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
                              >
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <p className="text-[10px] text-muted-foreground mt-4 pl-1">
                      These relays are used for discovery scanning only and do not affect your NIP-65 relay list or synced profile.
                    </p>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}

            {/* Stats row */}
            {isLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-20 rounded-lg" />
                ))}
              </div>
            ) : stats ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                <StatCard label="Total events" value={stats.total} icon={Hash} />
                <StatCard label="Active" value={stats.active} icon={FileText} accent="green" />
                <StatCard label="Deleted" value={stats.deleted} icon={Trash2} accent="red" />
                <StatCard label="Notes" value={stats.notes} icon={MessageCircle} />
                <StatCard label="Lists" value={stats.lists} icon={List} />
                <StatCard label="Sets" value={stats.sets} icon={Layers} />
              </div>
            ) : null}

            {/* Scanning indicator */}
            {isFetching && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 text-accent">
                    <div className="w-2 h-2 bg-accent rounded-full animate-pulse" />
                    <span>
                      Scanning {scanWider ? 'your relays + 18 discovery relays' : 'your configured relays'}...
                    </span>
                  </div>
                  {data?.scanInfo && (
                    <span className="font-mono text-muted-foreground">
                      {data.scanInfo.totalEventsFound} events • {data.scanInfo.pagesScanned} pages
                      {scanWider && data.scanInfo.discoveryRelayPages > 0 && (
                        <span className="text-accent/70 ml-1">({data.scanInfo.mainRelayPages} main + {data.scanInfo.discoveryRelayPages} discovery)</span>
                      )}
                    </span>
                  )}
                </div>
                <div className="h-1 bg-secondary rounded overflow-hidden">
                  <div
                    className="h-1 bg-gradient-to-r from-accent to-emerald-400 transition-all duration-300"
                    style={{
                      width: data?.scanInfo
                        ? `${Math.min(95, 30 + data.scanInfo.pagesScanned * 6)}%`
                        : '30%'
                    }}
                  />
                </div>
              </div>
            )}

            {/* Controls */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search events by title, content, or ID…"
                  className="pl-9 bg-secondary/40 border-border text-sm h-9"
                />
              </div>

              <div className="flex gap-2">
                {/* Kind filter dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-9 text-xs gap-1.5">
                      <SlidersHorizontal className="w-3.5 h-3.5" />
                      {selectedKinds.size > 0 ? `${selectedKinds.size} kinds` : 'Filter'}
                      <ChevronDown className="w-3 h-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuLabel className="text-xs">Event kinds</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {allKinds.map((kind) => (
                      <DropdownMenuCheckboxItem
                        key={kind}
                        checked={selectedKinds.has(kind)}
                        onCheckedChange={() => toggleKind(kind)}
                        className="text-xs"
                      >
                        <span className="flex items-center justify-between w-full">
                          <span>Kind {kind}</span>
                          <span className="text-muted-foreground text-[10px]">
                            {kindLabels[kind] || 'Unknown'}
                          </span>
                        </span>
                      </DropdownMenuCheckboxItem>
                    ))}
                    {allKinds.length === 0 && (
                      <div className="px-2 py-1.5 text-xs text-muted-foreground">No events found</div>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetch()}
                  disabled={isFetching}
                  className="h-9 text-xs"
                >
                  <RefreshCw className={cn('w-3.5 h-3.5', isFetching && 'animate-spin')} />
                  {isFetching ? ' Scanning...' : 'Refresh'}
                </Button>

                {/* Scan Wider toggle */}
                <div
                  className="flex items-center gap-2 h-9 px-3 rounded-md border border-border bg-secondary/30 text-xs cursor-pointer select-none"
                  onClick={() => !isFetching && setScanWider(!scanWider)}
                  title="When enabled, scans your relays plus 18 public discovery relays for maximum coverage"
                >
                  <Globe className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-muted-foreground">Wider</span>
                  <Switch
                    checked={scanWider}
                    disabled={isFetching}
                    className="data-[state=checked]:bg-accent"
                  />
                </div>

                {accumulatedEvents.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setAccumulatedEvents([]);
                      refetch();
                    }}
                    className="h-9 text-xs text-muted-foreground"
                  >
                    Reset scan
                  </Button>
                )}
              </div>
            </div>

            {/* Tree View */}
            <div className="pt-2">
              {/* npub root */}
              {user && (
                <div className="flex items-center gap-3 mb-6 pl-1 border-l-2 border-accent/30">
                  <div className="w-7 h-7 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-accent text-xs font-bold">@</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-sm text-accent truncate">
                      {(() => {
                        try {
                          return nip19.npubEncode(user.pubkey);
                        } catch {
                          return user.pubkey.slice(0, 12) + '...';
                        }
                      })()}
                    </div>
                    <p className="text-xs text-muted-foreground">Your Nostr events</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const npub = nip19.npubEncode(user.pubkey);
                      navigator.clipboard.writeText(npub);
                      toast({ title: 'Copied npub' });
                    }}
                    className="text-xs h-7 px-2"
                  >
                    Copy npub
                  </Button>
                </div>
              )}

              {/* Tree sections */}
              <div className="space-y-2">
                {/* Notes */}
                <TreeSection
                  id="notes"
                  icon={MessageCircle}
                  label="Notes"
                  sublabel="kind 1"
                  count={allEvents.filter(e => e.event.kind === 1).length}
                  openSections={openSections}
                  setOpenSections={setOpenSections}
                >
                  {allEvents.filter(e => e.event.kind === 1).map((scannedEvent) => (
                    <EventCard key={scannedEvent.event.id} {...scannedEvent} onRefresh={() => refetch()} />
                  ))}
                </TreeSection>

                {/* Social */}
                <TreeSection
                  id="social"
                  icon={ThumbsUp}
                  label="Social & Interactions"
                  sublabel="NIP-18/25/22"
                  count={allEvents.filter(e => SOCIAL_KINDS.includes(e.event.kind)).length}
                  openSections={openSections}
                  setOpenSections={setOpenSections}
                >
                  {allEvents.filter(e => SOCIAL_KINDS.includes(e.event.kind)).map((scannedEvent) => (
                    <EventCard key={scannedEvent.event.id} {...scannedEvent} onRefresh={() => refetch()} />
                  ))}
                </TreeSection>

                {/* Articles */}
                <TreeSection
                  id="articles"
                  icon={BookOpen}
                  label="Articles"
                  sublabel="NIP-23, kinds 30023/30024"
                  count={allEvents.filter(e => LONG_FORM_KINDS.includes(e.event.kind)).length}
                  openSections={openSections}
                  setOpenSections={setOpenSections}
                >
                  {allEvents.filter(e => LONG_FORM_KINDS.includes(e.event.kind)).map((scannedEvent) => (
                    <EventCard key={scannedEvent.event.id} {...scannedEvent} onRefresh={() => refetch()} />
                  ))}
                </TreeSection>

                {/* Media */}
                <TreeSection
                  id="media"
                  icon={Image}
                  label="Media"
                  sublabel="NIP-68/71"
                  count={allEvents.filter(e => MEDIA_KINDS.includes(e.event.kind)).length}
                  openSections={openSections}
                  setOpenSections={setOpenSections}
                >
                  {allEvents.filter(e => MEDIA_KINDS.includes(e.event.kind)).map((scannedEvent) => (
                    <EventCard key={scannedEvent.event.id} {...scannedEvent} onRefresh={() => refetch()} />
                  ))}
                </TreeSection>

                {/* Content */}
                <TreeSection
                  id="content"
                  icon={PenTool}
                  label="Content"
                  sublabel="highlights, wiki, code, polls, etc."
                  count={allEvents.filter(e => CONTENT_KINDS.includes(e.event.kind)).length}
                  openSections={openSections}
                  setOpenSections={setOpenSections}
                >
                  {allEvents.filter(e => CONTENT_KINDS.includes(e.event.kind)).map((scannedEvent) => (
                    <EventCard key={scannedEvent.event.id} {...scannedEvent} onRefresh={() => refetch()} />
                  ))}
                </TreeSection>

                {/* Lists */}
                <TreeSection
                  id="lists"
                  icon={List}
                  label="Lists"
                  sublabel="NIP-51 replaceable"
                  count={allEvents.filter(e => LIST_KINDS.includes(e.event.kind)).length}
                  openSections={openSections}
                  setOpenSections={setOpenSections}
                >
                  {allEvents.filter(e => LIST_KINDS.includes(e.event.kind)).map((scannedEvent) => (
                    <EventCard key={scannedEvent.event.id} {...scannedEvent} onRefresh={() => refetch()} />
                  ))}
                </TreeSection>

                {/* Sets */}
                <TreeSection
                  id="sets"
                  icon={Layers}
                  label="Sets"
                  sublabel="NIP-51 addressable"
                  count={allEvents.filter(e => SET_KINDS.includes(e.event.kind)).length}
                  openSections={openSections}
                  setOpenSections={setOpenSections}
                >
                  {allEvents.filter(e => SET_KINDS.includes(e.event.kind)).map((scannedEvent) => (
                    <EventCard key={scannedEvent.event.id} {...scannedEvent} onRefresh={() => refetch()} />
                  ))}
                </TreeSection>

                {/* Commerce */}
                <TreeSection
                  id="commerce"
                  icon={ShoppingBag}
                  label="Commerce"
                  sublabel="NIP-99 classifieds"
                  count={allEvents.filter(e => COMMERCE_KINDS.includes(e.event.kind)).length}
                  openSections={openSections}
                  setOpenSections={setOpenSections}
                >
                  {allEvents.filter(e => COMMERCE_KINDS.includes(e.event.kind)).map((scannedEvent) => (
                    <EventCard key={scannedEvent.event.id} {...scannedEvent} onRefresh={() => refetch()} />
                  ))}
                </TreeSection>

                {/* Calendar */}
                <TreeSection
                  id="calendar"
                  icon={CalendarIcon}
                  label="Calendar"
                  sublabel="NIP-52"
                  count={allEvents.filter(e => CALENDAR_KINDS.includes(e.event.kind)).length}
                  openSections={openSections}
                  setOpenSections={setOpenSections}
                >
                  {allEvents.filter(e => CALENDAR_KINDS.includes(e.event.kind)).map((scannedEvent) => (
                    <EventCard key={scannedEvent.event.id} {...scannedEvent} onRefresh={() => refetch()} />
                  ))}
                </TreeSection>

                {/* Live */}
                <TreeSection
                  id="live"
                  icon={Radio}
                  label="Live Streaming"
                  sublabel="NIP-53"
                  count={allEvents.filter(e => LIVE_KINDS.includes(e.event.kind)).length}
                  openSections={openSections}
                  setOpenSections={setOpenSections}
                >
                  {allEvents.filter(e => LIVE_KINDS.includes(e.event.kind)).map((scannedEvent) => (
                    <EventCard key={scannedEvent.event.id} {...scannedEvent} onRefresh={() => refetch()} />
                  ))}
                </TreeSection>

                {/* Git */}
                <TreeSection
                  id="git"
                  icon={GitBranch}
                  label="Git"
                  sublabel="NIP-34"
                  count={allEvents.filter(e => GIT_KINDS.includes(e.event.kind)).length}
                  openSections={openSections}
                  setOpenSections={setOpenSections}
                >
                  {allEvents.filter(e => GIT_KINDS.includes(e.event.kind)).map((scannedEvent) => (
                    <EventCard key={scannedEvent.event.id} {...scannedEvent} onRefresh={() => refetch()} />
                  ))}
                </TreeSection>

                {/* Badges */}
                <TreeSection
                  id="badges"
                  icon={Award}
                  label="Badges"
                  sublabel="NIP-58"
                  count={allEvents.filter(e => BADGE_KINDS.includes(e.event.kind)).length}
                  openSections={openSections}
                  setOpenSections={setOpenSections}
                >
                  {allEvents.filter(e => BADGE_KINDS.includes(e.event.kind)).map((scannedEvent) => (
                    <EventCard key={scannedEvent.event.id} {...scannedEvent} onRefresh={() => refetch()} />
                  ))}
                </TreeSection>

                {/* Deleted */}
                <Collapsible open={openSections.has('deleted')} onOpenChange={(open) => {
                  const next = new Set(openSections);
                  if (open) next.add('deleted'); else next.delete('deleted');
                  setOpenSections(next);
                }}>
                  <CollapsibleTrigger className="flex w-full items-center gap-2 py-2 px-3 hover:bg-secondary/50 rounded-lg group border-t border-border/50 pt-4">
                    <ChevronRight className={cn("w-4 h-4 transition-transform", openSections.has('deleted') ? "rotate-90" : "")} />
                    <Trash2 className="w-4 h-4 text-accent" />
                    <span className="font-medium">Deleted</span>
                    <span className="ml-auto text-xs bg-accent/10 text-accent px-2 py-0.5 rounded font-mono">
                      {allEvents.filter(e => e.isDeleted).length}
                    </span>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="ml-6 pl-6 border-l border-accent/20 space-y-3 pt-1">
                    {allEvents
                      .filter(e => e.isDeleted)
                      .map((scannedEvent) => (
                        <EventCard key={scannedEvent.event.id} {...scannedEvent} onRefresh={() => refetch()} />
                      ))}
                  </CollapsibleContent>
                </Collapsible>
              </div>
            </div>
          </div>
        )}
      </div>
    </TombstoneLayout>
  );
}

// ─── Sub-components ───────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  accent?: 'green' | 'red' | 'amber';
}

function StatCard({ label, value, icon: Icon, accent }: StatCardProps) {
  const accentClass = accent === 'green'
    ? 'text-accent'
    : accent === 'red'
    ? 'text-destructive'
    : accent === 'amber'
    ? 'text-amber-400'
    : 'text-muted-foreground';

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{label}</span>
        <Icon className={cn('w-3.5 h-3.5', accentClass)} />
      </div>
      <p className={cn('text-2xl font-display font-semibold tracking-tight', accentClass === 'text-muted-foreground' ? 'text-foreground' : accentClass)}>
        {value.toLocaleString()}
      </p>
    </div>
  );
}

interface TreeSectionProps {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  sublabel: string;
  count: number;
  openSections: Set<string>;
  setOpenSections: React.Dispatch<React.SetStateAction<Set<string>>>;
  children: React.ReactNode;
}

function TreeSection({ id, icon: Icon, label, sublabel, count, openSections, setOpenSections, children }: TreeSectionProps) {
  return (
    <Collapsible open={openSections.has(id)} onOpenChange={(open) => {
      const next = new Set(openSections);
      if (open) next.add(id); else next.delete(id);
      setOpenSections(next);
    }}>
      <CollapsibleTrigger className="flex w-full items-center gap-2 py-2 px-3 hover:bg-secondary/50 rounded-lg group">
        <ChevronRight className={cn("w-4 h-4 transition-transform", openSections.has(id) ? "rotate-90" : "")} />
        <Icon className="w-4 h-4 text-accent" />
        <span className="font-medium">{label} <span className="text-xs text-accent/70">({sublabel})</span></span>
        <span className="ml-auto text-xs bg-secondary px-2 py-0.5 rounded font-mono text-muted-foreground">
          {count}
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent className="ml-6 pl-6 border-l border-accent/20 space-y-3 pt-1">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

function LandingHero() {
  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center text-center space-y-10 animate-fade-up">
      {/* Grid background */}
      <div className="absolute inset-0 tombstone-grid-bg pointer-events-none opacity-40" />

      <div className="relative space-y-6 max-w-lg">
        {/* Logo icon */}
        <div className="mx-auto relative flex items-center justify-center w-24 h-24">
          <div className="absolute inset-0 rounded-xl bg-[hsl(var(--accent))]/15 blur-2xl" />
          <img src="/tombstone_icon.png" alt="Tombstone" className="relative w-20 h-20 object-contain" />
        </div>

        <div className="space-y-3">
          <div className="flex justify-center">
            <img 
              src="/tombstone_typography.png" 
              alt="Tombstone" 
              className="h-20 sm:h-24 object-contain"
            />
          </div>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Take back control of your Nostr footprint.
            <br />
            Delete events, track relay propagation, nullify lingering data.
          </p>
        </div>

        {/* Features list */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-left">
          {[
            {
              icon: Trash2,
              title: 'NIP-09 Deletion',
              desc: 'Publish cryptographic deletion requests for notes, articles, and lists.',
            },
            {
              icon: SlidersHorizontal,
              title: 'Relay Tracking',
              desc: 'See which relays still carry your events after deletion.',
            },
            {
              icon: RefreshCw,
              title: 'Nullification',
              desc: 'Overwrite replaceable events with empty content as a fallback.',
            },
          ].map((feat) => (
            <div
              key={feat.title}
              className="rounded-lg border border-border bg-card/50 p-3.5 space-y-1.5"
            >
              <feat.icon className="w-4 h-4 text-[hsl(var(--accent))]" />
              <p className="text-sm font-medium text-foreground">{feat.title}</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{feat.desc}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="flex justify-center pt-2">
          <LoginArea className="max-w-72" />
        </div>

        <p className="text-xs text-muted-foreground">
          Connect your Nostr identity to scan and manage your events.
        </p>
      </div>
    </div>
  );
}
